import React, { useEffect } from 'react'

import { useSelector } from 'react-redux'

import { cpfValue, formatCurrency, TemplatesPDF } from '@interco/cp-react-ui-lib'
import { ApplicationState } from '@store/types'
import { ConvertDateUTC, DateFormat } from '@utils/functions'
import { CCBEnum } from '@utils/enums'
import { BridgeService } from '@services/bridge'

import { TextCCB } from './Clausulas'

interface DadosProposta {
  nomeCliente: string
  cpf: string
  dataNascimento?: string
  estadoCivil?: string
  documentoIdentificacao?: string
  enderecoResidencial?: string
  numeroResidencial?: string
  bairroResidencial?: string
  cidadeResidencial?: string
  estadoResidencial?: string
  cidadeNaturalidade?: string
  nacionalidade?: string
  nomeMae?: string
  nomePai?: string
  banco?: string
  agencia?: string
  conta?: string
  valorTotalASerPago?: string
  taxaEfetivaMensal?: string
  taxa?: string
  taxaEfetivaAnual?: string
  taxaNominalAnual?: string
  taxaNominalContrato?: string
  valorSolicitado?: string
  valorSolicitadoPercentual?: string
  atributosCetMensal?: string
  atributosCetAnual?: string
  tarifaCadastro?: string
  taxaTarifaCadastro?: string
  dataEmissao?: string
  valorIOF?: string
  percentualIOF?: string
  tipoLiberacao?: string
  jurosContratados?: string
  jurosContratadosPercentual?: string
  valorDoMutuo?: string
  valorTotalDestinadoPagamentoSeguro?: string
  percentualPropostaSeguroPrestamista?: string
  percentualValorSeguro?: string
  possuiSeguro?: boolean
  matricula?: string
  quantidadeDeParcelas?: number
  valorParcela?: string
  primeiroDesconto?: string
  ultimoDesconto?: string
}

const formatCurrencyValue = (value: string | undefined): string => {
  if (!value) return 'R$ 0,00'

  const numericValue = parseFloat(value.replace('.', '').replace(',', '.'))
  return formatCurrency(numericValue)
}

const formatPercentage = (value: string | undefined, fallback = '0'): string => {
  const numericValue = parseFloat((value ?? fallback).replace(',', '.'))
  return `${numericValue.toFixed(2)}%`
}

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return ''
  return DateFormat(new Date(dateString), 'dd/MM/yyyy')
}

const formatDateWithTime = (dateString: string | undefined): string => {
  if (!dateString) return ''
  return DateFormat(new Date(`${dateString}T00:00`), 'dd/MM/yyyy')
}

const formatDateUTC = (dateString: string | undefined): string => {
  if (!dateString) return ''
  return DateFormat(ConvertDateUTC(dateString), 'MM/yyyy')
}

const buildEmitenteData = (dadosProposta: DadosProposta) => ({
  nome: dadosProposta.nomeCliente,
  nomeSocial: dadosProposta.nomeCliente,
  cpf: cpfValue(dadosProposta.cpf),
  dataNascimento: formatDate(dadosProposta.dataNascimento),
  estadoCivil: dadosProposta?.estadoCivil,
  documentoIdentificacao: dadosProposta?.documentoIdentificacao,
  endereco: `${dadosProposta?.enderecoResidencial}, ${dadosProposta?.numeroResidencial}`,
  naturalidade: dadosProposta?.cidadeNaturalidade,
  nacionalidade: dadosProposta?.nacionalidade,
  nomeMae: dadosProposta?.nomeMae,
  nomePai: dadosProposta?.nomePai,
  banco: dadosProposta?.banco,
  agencia: dadosProposta?.agencia,
  contaCorrente: dadosProposta?.conta,
})

const buildDemonstrativoCalculo = (dadosProposta: DadosProposta) => ({
  valorTotal: formatCurrencyValue(dadosProposta?.valorTotalASerPago),
  taxaJurosMensal: formatPercentage(dadosProposta?.taxaEfetivaMensal ?? dadosProposta?.taxa),
  taxaJurosAnual: formatPercentage(
    dadosProposta?.taxaEfetivaAnual ?? dadosProposta?.taxaNominalAnual,
  ),
  taxaNominalJurosMensal: formatPercentage(
    dadosProposta?.taxaNominalContrato ?? dadosProposta?.taxa,
  ),
  taxaNominalJurosAnual: formatPercentage(dadosProposta?.taxaNominalAnual),
  valorSolicitado: formatCurrencyValue(dadosProposta.valorSolicitado),
  percentualValorSolicitado: `${dadosProposta.valorSolicitadoPercentual}%`,
  cetMensal: `${dadosProposta.atributosCetMensal}%`,
  cetAnual: `${dadosProposta.atributosCetAnual}%`,
  tarifaCadastro: formatCurrencyValue(dadosProposta.tarifaCadastro),
  percentualTarifaCadastro: `${dadosProposta.taxaTarifaCadastro}%`,
  dataEmissao: formatDateWithTime(dadosProposta.dataEmissao),
  iof: formatCurrencyValue(dadosProposta.valorIOF),
  percentualIof: `${dadosProposta.percentualIOF}%`,
  liberacaoCredito: `${dadosProposta.tipoLiberacao}`,
  jurosContratados: formatCurrencyValue(dadosProposta.jurosContratados),
  percentualJurosContratados: `${dadosProposta.jurosContratadosPercentual}%`,
  valorMutuo: formatCurrencyValue(dadosProposta?.valorDoMutuo),
  pracaPagamento: `${dadosProposta.cidadeResidencial}/${dadosProposta.estadoResidencial}`,
  valorSeguro: formatCurrencyValue(dadosProposta.valorTotalDestinadoPagamentoSeguro),
  percentualPropostaSeguroPrestamista: formatPercentage(
    dadosProposta.percentualPropostaSeguroPrestamista ?? dadosProposta.percentualValorSeguro,
  ),
  seguro: dadosProposta.possuiSeguro ? 'Sim' : 'Não',
  matricula: dadosProposta.matricula,
  qtdeParcelasMensais: dadosProposta.quantidadeDeParcelas,
  valorParcelaMensal: formatCurrencyValue(dadosProposta.valorParcela),
  previsaoPrimeiroDesconto: formatDateUTC(dadosProposta?.primeiroDesconto),
  previsaoUltimoDesconto: formatDateUTC(dadosProposta?.ultimoDesconto),
  textAcordo:
    '2.24 -  O (A) EMITENTE declara estar ciente e de acordo que, na hipótese de haver divergência no valor do contrato negociado, por motivo de indisponibilidade de margem integral para averbação do mútuo, o CREDOR poderá realizar a operação nas novas condições. Dessa forma, as condições solicitadas nessa proposta podem ser alteradas em razão do limite disponível de margem e, havendo alteração, o(a) EMITENTE concorda com a variação. Assim que a operação for aprovada, o(a) EMITENTE receberá uma comunicação do CREDOR detalhando as condições pactuadas e, caso haja discordância, o(a) EMITENTE  poderá solicitar o cancelamento em até 7 dias.',
})

const CCBCtps = (): React.JSX.Element => {
  const { numeroProposta, dadosProposta } = useSelector(
    (state: ApplicationState) => state.refinanciamento.proposta,
  )
  const { convenioSelecionado } = useSelector(
    (state: ApplicationState) => state.refinanciamento.contratos,
  )

  const variant = 'web'

  useEffect(() => {
    BridgeService.tac({
      name: 'termos_CCB',
      actions: ['exibir_termo_contratacao'],
    })
  }, [convenioSelecionado])

  return (
    <TemplatesPDF.CedulaCreditoBancariov2
      variant={variant}
      typeCCB={CCBEnum.CCB}
      header={{ numDoc: String(numeroProposta) }}
      emitente={buildEmitenteData(dadosProposta)}
      demonstrativoCalculo={buildDemonstrativoCalculo(dadosProposta)}
      textoAutorizacao={{ text: TextCCB }}
      assinatura={{
        isDgAssinado: true,
      }}
      footer={{
        atualizado: new Date('2025/05/14'),
        codigo: 'CP105',
        edicao: '1.2.0',
        contatos: [
          'Central de Atendimento 3003-4070 (Capitais e Regiões Metropolitanas) SAC',
          '0800 940 9999 Ouvidoria 0800 940 7772 https://inter.co/',
          'Deficiente Fala e Audição https://inter.co/atendimento-em-libras/',
        ],
      }}
    />
  )
}

export default CCBCtps
